import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Button, Text, TextInput, SegmentedButtons, Card } from 'react-native-paper';
import { useTranslation } from '@/lib/useTranslation';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/hooks/useTheme';

type Report = {id:string; subject:string; location:string; reason:string; status:string; resolution:string};
export default function SafetyCenter({initialLocation='',onBack,onManageUsers}:{initialLocation?:string;onBack:()=>void;onManageUsers?:()=>void}) {
  const {t,isRTL}=useTranslation('common');
  const {theme}=useTheme();
  const [subject,setSubject]=useState('content');
  const [location,setLocation]=useState(initialLocation);
  const [reason,setReason]=useState(''); const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState(''); const [reports,setReports]=useState<Report[]>([]);
  const [moderator,setModerator]=useState(false);
  const [notes,setNotes]=useState<Record<string,string>>({});
  const refresh=useCallback(async()=>{
    const {data,error}=await supabase.from('content_reports' as any).select('id,subject,location,reason,status,resolution').order('created_at',{ascending:false}).limit(100);
    if(error) throw error; setReports((data||[]) as unknown as Report[]);
  },[]);
  useEffect(()=>{let live=true;
    supabase.rpc('content_moderator' as any).then(({data})=>{if(live)setModerator(data===true);});
    refresh().catch(()=>{if(live)setMessage(t('safety.loadFailed'));});
    return()=>{live=false;};
  },[refresh,t]);
  async function submit(){
    if(busy)return; setBusy(true);setMessage('');
    try{
      const {error}=await supabase.from('content_reports' as any).insert({subject,location:location.trim(),reason:reason.trim()});
      if(error)throw error;setReason('');setLocation('');setMessage(t('safety.sent'));await refresh();
    }catch{setMessage(t('safety.failed'));}finally{setBusy(false);}
  }
  async function review(id:string,status:string){
    setBusy(true);setMessage('');
    try{
      const {data,error}=await supabase.from('content_reports' as any).update({status,resolution:notes[id]||''}).eq('id',id).select('id');
      if(error||!data?.length)throw error||new Error('Access denied');await refresh();
    }catch{setMessage(t('safety.failed'));}finally{setBusy(false);}
  }
  async function moderate(id:string,hidden:boolean){
    setBusy(true);setMessage('');
    try {
      const {error}=await supabase.rpc('moderate_maintenance_report' as any,{report_id:id,hidden,explanation:notes[id]||''});
      if(error)throw error;await refresh();
    }catch{setMessage(t('safety.failed'));}finally{setBusy(false);}
  }
  return <ScrollView style={{flex:1,backgroundColor:theme.colors.background}} contentContainerStyle={{padding:20,gap:16,direction:isRTL?'rtl':'ltr'}}>
    <Button onPress={onBack}>{t('safety.back')}</Button>
    <Text variant="headlineSmall">{t('safety.title')}</Text>
    <Text>{t('safety.intro')}</Text>
    <SegmentedButtons value={subject} onValueChange={setSubject} buttons={['content','user'].map(value=>({value,label:t(`safety.${value}`)}))}/>
    <TextInput label={t('safety.location')} value={location} onChangeText={setLocation} maxLength={500}/>
    <TextInput label={t('safety.reason')} value={reason} onChangeText={setReason} multiline maxLength={3000}/>
    <Button mode="contained" loading={busy} disabled={busy||location.trim().length<3||reason.trim().length<10} onPress={submit}>{t('safety.send')}</Button>
    {!!message&&<Text accessibilityLiveRegion="polite">{message}</Text>}
    <Text variant="titleLarge">{t(moderator?'safety.queue':'safety.mine')}</Text>
    <Button onPress={()=>refresh().catch(()=>setMessage(t('safety.loadFailed')))}>{t('safety.refresh')}</Button>
    {moderator&&<Text>{t('safety.moderation')}</Text>}
    {moderator&&onManageUsers&&<Button onPress={onManageUsers}>{t('safety.manageUsers')}</Button>}
    {reports.map(report=><Card key={report.id}><Card.Content style={{gap:8}}>
      <Text variant="titleMedium">{report.location}</Text><Text>{report.reason}</Text>
      <Text>{t(`safety.${report.status}`)}</Text>
      {!!report.resolution&&<Text>{report.resolution}</Text>}
      {moderator&&<View style={{gap:8}}>
        <TextInput label={t('safety.action')} value={notes[report.id]??report.resolution} onChangeText={value=>setNotes(old=>({...old,[report.id]:value}))} multiline maxLength={3000}/>
        {/^maintenance\/[0-9a-f-]{36}$/i.test(report.location)&&<>
          <Button disabled={busy||(notes[report.id]||'').trim().length<10} onPress={()=>moderate(report.id,true)}>{t('safety.hide')}</Button>
          <Button disabled={busy||(notes[report.id]||'').trim().length<10} onPress={()=>moderate(report.id,false)}>{t('safety.restore')}</Button>
        </>}
        <Button disabled={busy} onPress={()=>review(report.id,'reviewing')}>{t('safety.reviewing')}</Button>
        {['resolved','dismissed'].map(status=><Button key={status} disabled={busy||(notes[report.id]||'').trim().length<10} onPress={()=>review(report.id,status)}>{t(`safety.${status}`)}</Button>)}
      </View>}
    </Card.Content></Card>)}
  </ScrollView>;
}
